import React, { useState } from "react";
import { Flex, Grid, Text } from "@chakra-ui/react";
import { useQueryClient } from "@tanstack/react-query";
import AppButton from "@/features/line-sheet-sets/app-button";
import useI18nHelper from "@/features/ui/hooks/use-i18n-helper";
import PathPickProductCard, {
  PathPickProductCardHighlight,
} from "@/features/pathpick/components/product/PathPickProductCard";
import PathPickProductCardViewSettingsButton from "@/features/pathpick/components/product/PathPickProductCardViewSettingsButton";
import PathPickSizeStockModal from "@/features/pathpick/components/product/PathPickSizeStockModal";
import PathPickStartGuideCard from "@/features/pathpick/features/start-guide/components/PathPickStartGuideCard";
import PathPickStartGuideDisabledHint from "@/features/pathpick/features/start-guide/components/PathPickStartGuideDisabledHint";
import PathPickStartGuideStepPanel from "@/features/pathpick/features/start-guide/components/PathPickStartGuideStepPanel";
import PathPickStartGuideMarketBasisSelector from "@/features/pathpick/features/start-guide/components/product-tour/PathPickStartGuideMarketBasisSelector";
import {
  DEMO_KREAM_OPTIONS,
  DEMO_POIZON_OPTIONS,
  DEMO_PRODUCTS,
} from "@/features/pathpick/features/start-guide/utils/demoProducts";
import {
  PathPickMarketPriceBasis,
  PathPickProduct,
  PathPickProductCardViewSettings,
} from "@/features/pathpick/types";
import { START_GUIDE_CARD_VIEW } from "@/features/pathpick/features/start-guide/constants";

interface Props {
  pickedIds: number[];
  onPick: (id: number) => void;
  onPrev: () => void;
  onNext: () => void;
}

const TOTAL_STEPS = 5;
const NEXT_HINT_ID = "start-guide-product-tour-hint";

// 보기 설정을 직접 바꿔 보는 단계 — 이 단계부터 카드가 고정값 대신 사용자가 고른 값을 따른다.
const CARD_VIEW_STEP = 4;

// 단계별로 카드의 어느 영역을 파란 선으로 감쌀지 (전달서 §9).
const STEP_HIGHLIGHT: Record<number, PathPickProductCardHighlight | undefined> =
  {
    // 일반 vs 핫딜 — 핫딜 배지가 얹히는 썸네일
    1: "thumbnail",
    2: "metrics",
    // 기준을 바꾸면 값이 움직이는 두 마켓 비교 블록과 추천 줄
    3: "comparison",
    // 보기 설정이 켜고 끄는 대상이 바로 그 비교 블록이다
    4: "comparison",
    5: "pick",
  };

/**
 * 상품 찾기 안내 — 운영과 같은 상품 카드로 진행하는 5단계 교육.
 *
 * 카드는 운영 화면과 같은 `PathPickProductCard` 를 그대로 쓴다. 마진을 카드가 직접
 * (KREAM가 − 공급가)로 계산하기 때문에 기준을 바꾸면 API 없이 즉시 다시 계산된다.
 */
function PathPickStartGuideProductTour({
  pickedIds,
  onPick,
  onPrev,
  onNext,
}: Props) {
  const { t } = useI18nHelper();
  const queryClient = useQueryClient();

  const [step, setStep] = useState(1);
  const [marketPriceBasis, setMarketPriceBasis] =
    useState<PathPickMarketPriceBasis>("bidPrice");
  const [sizeStockItem, setSizeStockItem] = useState<PathPickProduct | null>(
    null
  );
  // 4/5 에서 사용자가 고르는 보기 설정 — 이 화면 안에서만 산다.
  // 실제 보기 설정(localStorage)은 건드리지 않는다: 가이드는 언제 들어와도 같은 화면에서 시작해야 한다.
  const [cardView, setCardView] = useState<PathPickProductCardViewSettings>(
    START_GUIDE_CARD_VIEW
  );

  // 일반 상품과 핫딜 상품의 차이를 둘 다 담아 봐야 체험이 성립한다 — 한쪽만으로는 넘어가지 않는다.
  const hasPickedAll = DEMO_PRODUCTS.every((item) =>
    pickedIds.includes(item.id)
  );

  // 상품 상세 정보 모달은 크림·포이즌 옵션을 서버에서 조회한다.
  // 열기 직전에 목 응답을 캐시에 심어 두면 모달을 고치지 않고도 네트워크 없이 돈다.
  const openSizeStock = (item: PathPickProduct) => {
    const kreamOptions = DEMO_KREAM_OPTIONS[item.id];
    if (kreamOptions) {
      queryClient.setQueryData(
        ["pathpick", "kream-options", item.id],
        kreamOptions
      );
    }
    const poizonOptions = DEMO_POIZON_OPTIONS[item.id];
    if (poizonOptions) {
      queryClient.setQueryData(
        ["pathpick", "poizon-options", item.id],
        poizonOptions
      );
    }
    setSizeStockItem(item);
  };

  const isLastStep = step === TOTAL_STEPS;

  // 1~3 은 설명이 비교 블록에 걸려 있다 — 4/5 에서 비교를 끈 채 되돌아가도 그 단계들은 그대로 보여야 한다.
  const effectiveCardView =
    step >= CARD_VIEW_STEP ? cardView : START_GUIDE_CARD_VIEW;

  // 이동 버튼은 단계 패널의 우측 하단에 붙는다 — 다른 탭과 같다.
  // 1/5 의 이전은 탭 밖(설정)으로 나가고, 그 뒤로는 이 탭 안에서만 움직인다.
  const navigation = (
    <Flex flexDirection={"column"} gap={"6px"} alignItems={"flex-end"}>
      <Flex gap={"8px"} alignItems={"center"} flexWrap={"wrap"}>
        <AppButton
          variant={"normal"}
          onClick={step > 1 ? () => setStep(step - 1) : onPrev}
        >
          {t(
            step > 1
              ? "path_pick_guide:nav.prev"
              : "path_pick_guide:nav.prev_step"
          )}
        </AppButton>
        {isLastStep ? (
          <AppButton
            variant={"primary"}
            isDisabled={!hasPickedAll}
            onClick={onNext}
            chakraProps={
              hasPickedAll ? undefined : { "aria-describedby": NEXT_HINT_ID }
            }
          >
            {t("path_pick_guide:nav.next_step")}
          </AppButton>
        ) : (
          <AppButton variant={"primary"} onClick={() => setStep(step + 1)}>
            {t("path_pick_guide:nav.next")}
          </AppButton>
        )}
      </Flex>
      {isLastStep && !hasPickedAll && (
        <PathPickStartGuideDisabledHint id={NEXT_HINT_ID} textAlign={"right"}>
          {t("path_pick_guide:product_tour.next_disabled_hint")}
        </PathPickStartGuideDisabledHint>
      )}
    </Flex>
  );

  return (
    <>
      <PathPickStartGuideCard
        header={
          <Flex flexDirection={"column"} gap={"6px"}>
            <Text
              textStyle={"body-lg-bold"}
              fontSize={"22px"}
              color={"#1D1D1B"}
            >
              {t("path_pick_guide:product_tour.title")}
            </Text>
            <Text textStyle={"body-sm-medium"} color={"#6F6F6C"}>
              {t("path_pick_guide:product_tour.description")}
            </Text>
          </Flex>
        }
        headerProps={{
          paddingBottom: "0px",
        }}
      >
        <Flex flexDirection={"column"} gap={"16px"}>
          <PathPickStartGuideStepPanel
            stepLabel={t("path_pick_guide:nav.step_label", {
              current: step,
              total: TOTAL_STEPS,
            })}
            stepAriaLabel={t("path_pick_guide:nav.step_aria_label")}
            title={t(`path_pick_guide:product_tour.step_${step}.title`)}
            description={t(
              `path_pick_guide:product_tour.step_${step}.description`
            )}
            actions={navigation}
          />

          <Flex
            gap={"16px"}
            alignItems={"center"}
            flexDirection={{ base: "column", lg: "row" }}
          >
            {/* 카드는 실제 패스픽 카드와 같은 컴팩트 폭을 유지한다 —
                큰 화면에서 가로로 늘어나면 안 된다(전달서 §9). */}
            <Grid
              templateColumns={"repeat(2, minmax(200px, 240px))"}
              gap={"6px"}
              flexShrink={0}
            >
              {DEMO_PRODUCTS.map((item) => (
                <PathPickProductCard
                  key={item.id}
                  item={item}
                  marketPriceBasis={marketPriceBasis}
                  viewOverride={effectiveCardView}
                  isPicked={pickedIds.includes(item.id)}
                  onOpenSizeStock={() => openSizeStock(item)}
                  // 담기는 5/5(담기 안내)에 와야 동작한다 — 그전에는 눌러도 아무 일이 없다.
                  onPick={isLastStep ? () => onPick(item.id) : undefined}
                  highlightedRegion={STEP_HIGHLIGHT[step]}
                  isOptionsPrefetchDisabled={true}
                />
              ))}
            </Grid>

            {/* 기준 선택기는 카드 오른쪽 상하 가운데에 둔다(전달서 §9 3/4). */}
            {step === 3 && (
              <Flex
                flexDirection={"column"}
                alignItems={"center"}
                gap={"8px"}
                minWidth={0}
              >
                <PathPickStartGuideMarketBasisSelector
                  value={marketPriceBasis}
                  onChange={setMarketPriceBasis}
                />
                <Text textStyle={"caption-medium"} color={"#6F6F6C"}>
                  {t("path_pick_guide:product_tour.market_basis_hint")}
                </Text>
              </Flex>
            )}

            {/* 보기 설정도 같은 자리에 둔다. 운영 화면과 같은 버튼·같은 모달을 그대로 쓴다 —
                어디를 눌러야 하는지까지 알려주는 것이 이 단계의 목적이다.
                저장만 끊어서(override) 왼쪽 두 카드에만 반영한다. */}
            {step === CARD_VIEW_STEP && (
              <Flex
                flexDirection={"column"}
                alignItems={"center"}
                gap={"8px"}
                minWidth={0}
              >
                <PathPickProductCardViewSettingsButton
                  override={{ value: cardView, onApply: setCardView }}
                />
                <Text textStyle={"caption-medium"} color={"#6F6F6C"}>
                  {t("path_pick_guide:product_tour.card_view_hint")}
                </Text>
              </Flex>
            )}
          </Flex>
        </Flex>
      </PathPickStartGuideCard>

      {sizeStockItem && (
        <PathPickSizeStockModal
          isOpen={true}
          onClose={() => setSizeStockItem(null)}
          item={sizeStockItem}
          viewOverride={effectiveCardView}
        />
      )}
    </>
  );
}

export default PathPickStartGuideProductTour;
